import { ReactNode, useState } from 'react'

import { useAmslerGridImage } from './amsler-grid-card-image.hook'
import AmslerGridModal from './amsler-grid-modal.component'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getFormattedDate } from 'src/utils/date-formatter.util'
import { AmslerGrid } from './amsler-grid.component'
import {
  CurecallBadge,
  CurecallButton,
  ECurecallBadgeColor,
  ECurecallButtonAppearance,
} from '@honestica/curecall-ui'
import Card, { CardProps } from 'src/components/card/card.component'
import { TPatientForm } from 'src/types/form'
import { pleaseDeleteMe, TNeiVariableGroup } from '../nei/nei-card.component'

const FETCH_BY = 10

const AmslerGridCardWrapper = ({ children, cardProps }: { children: ReactNode; cardProps?: CardProps }) => {
  const { t } = useTranslation()
  return (
    <div>
      <Card title={t('components.patient.patient-file.body.followup-details.amsler-grid')} {...cardProps}>
        <div className="c-mt-2">{children}</div>
      </Card>
    </div>
  )
}

const AmslerGridCard = ({ forms }: { forms: TPatientForm[] }) => {
  const { patientId, projectId } = useParams()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [page, setPage] = useState(1)
  const [history, setHistory] = useState(forms?.slice(0, FETCH_BY))
  const { t } = useTranslation()

  const onToggle = () => {
    setIsModalOpen(!isModalOpen)
  }

  const firstForm = forms?.[0]
  const lastFormdate = firstForm?.updatedAt as string
  const lastPatientFormId = firstForm?.id
  const variables = pleaseDeleteMe(firstForm?.PatientVariables) as unknown as Record<string, TNeiVariableGroup[]>
  const lastPatientVariables = variables.amslergrid

  const rightEye = lastPatientVariables?.find(
    (item) => item.name === 'amslerGridDistordedBlurredOrMissingAreasR',
  )

  const leftEye = lastPatientVariables?.find(
    (item) => item.name === 'amslerGridDistordedBlurredOrMissingAreasL',
  )

  const {
    isLoading: isLoadingRightImage,
    error: rightImageError,
    image: rightImage,
  } = useAmslerGridImage(
    rightEye?.VariableId ?? 0,
    lastPatientFormId as number,
    patientId as string,
    projectId as string,
  )

  const {
    isLoading: isLoadingLeftImage,
    error: leftImageError,
    image: leftImage,
  } = useAmslerGridImage(
    leftEye?.VariableId ?? 0,
    lastPatientFormId as number,
    patientId as string,
    projectId as string,
  )

  if (isLoadingLeftImage || isLoadingRightImage) {
    return <div className="loading" />
  }

  if (leftImageError || rightImageError)
    return <p>{t('components.patient.patient-file.body.followup-details.no-amsler-grid-to-show')}</p>

  const displayMore = () => {
    const nextPage = page + 1
    setHistory(forms.slice(0, nextPage * FETCH_BY))
    setPage(nextPage)
  }

  return (
    <>
      {isModalOpen && (
        <AmslerGridModal
          onToggle={onToggle}
          history={history}
          displayMore={displayMore}
        />
      )}

      <AmslerGridCardWrapper
        cardProps={{
          topRight: (
            <CurecallButton
              onClick={() => onToggle()}
              appearance={ECurecallButtonAppearance['PRIMARY-OUTLINED']}
            >
              {t('components.patient.patient-file.body.followup-details.history')}
            </CurecallButton>
          ),
        }}
      >
        <p className="-c-mt-2 c-pb-2">
          {t('components.patient.patient-file.body.followup-details.test-date', {
            date: getFormattedDate(new Date(lastFormdate)),
          })}
        </p>

        <div className="c-flex">
          <div>
            <h4 className="c-font-bold c-mb-3">
              {t('components.patient.patient-file.body.followup-details.left-eye')}
            </h4>
            <AmslerGrid imageUrl={leftImage} />
          </div>

          <div className="c-flex c-flex-col c-justify-center c-items-center c-mx-4">
            {(leftEye?.gridCenterIsHit || rightEye?.gridCenterIsHit) && (
              <CurecallBadge color={ECurecallBadgeColor.RED} className="tw-text-sm tw-px-2">
                {t('components.patient.patient-file.body.followup-details.center-zone-impacted')}
              </CurecallBadge>
            )}
            <p className="c-m-0 c-font-bold">
              {t('components.patient.patient-file.body.followup-details.left-eye-with-answer', {
                patientAnswer: leftEye?.patientAnswer || 0,
              })}
            </p>
            <p className="c-m-0 c-font-bold">
              {t('components.patient.patient-file.body.followup-details.right-eye-with-answer', {
                patientAnswer: rightEye?.patientAnswer || 0,
              })}
            </p>
          </div>

          <div>
            <h4 className="c-font-bold c-mb-3">
              {t('components.patient.patient-file.body.followup-details.right-eye')}
            </h4>
            <AmslerGrid imageUrl={rightImage} />
          </div>
        </div>
      </AmslerGridCardWrapper>
    </>
  )
}

export default AmslerGridCard
